import { useState, useCallback, useEffect, useRef } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from './hooks/useAuth.ts'
import { useTrace } from './hooks/useTrace.ts'
import { checkSetupNeeded } from './api/auth.ts'
import { Header } from './components/layout/Header.tsx'
import { Sidebar } from './components/layout/Sidebar.tsx'
import { StatusBar } from './components/layout/StatusBar.tsx'
import { MapView } from './components/map/MapView.tsx'
import { DetailPanel } from './components/details/DetailPanel.tsx'
import { SearchBar } from './components/search/SearchBar.tsx'
import { SetupPage } from './components/auth/SetupPage.tsx'
import { AccountPage } from './components/auth/AccountPage.tsx'
import { AddUserPage } from './components/auth/AddUserPage.tsx'
import { UserManagementPage } from './components/auth/UserManagementPage.tsx'
import { ProtectedRoute, LoginRoute } from './components/auth/RouteGuards.tsx'
import { DiscrepancyDashboard } from './components/imports/DiscrepancyDashboard.tsx'
import { QueuePage } from './components/gis-queue/QueuePage.tsx'
import { EngineeringDashboard } from './components/engineering/EngineeringDashboard.tsx'
import { SecondaryReviewDashboard } from './components/secondary/SecondaryReviewDashboard.tsx'
import { OutagesDashboard } from './components/oms/OutagesDashboard.tsx'
import { ReliabilityMetricsPage } from './components/oms/ReliabilityMetricsPage.tsx'
import { OutageHistoryPage } from './components/oms/OutageHistoryPage.tsx'
import { UnmatchedSignalsPage } from './components/oms/UnmatchedSignalsPage.tsx'
import { PowertrxExportPage } from './components/oms/PowertrxExportPage.tsx'
import { AttentionPage } from './components/home/AttentionPage.tsx'
import { legacyFeatureRedirectTarget } from './utils/legacyFeatureRedirect.ts'
import { appPath } from './appConfig.ts'
import { SelectedFeederProvider } from './components/feeder/SelectedFeederProvider.tsx'
import { useSelectedFeeder } from './hooks/useSelectedFeeder.ts'
import { zoomForSearchResult, type SearchZoomTarget } from './components/search/searchZoom.ts'
import { parseFeatureParam } from './utils/discrepancyLink.ts'
import type { SearchHighlight } from './components/map/SearchHighlightOverlay.tsx'

interface SearchResult {
  type: string
  id: number
  label: string
  sublabel: string | null
  // Point for meters/transformers/substations; LineString for feeder routes
  location: { type: string; coordinates: number[] | number[][] } | null
}

function MapPage({ onMouseCoords }: { onMouseCoords: (coords: { lat: number; lng: number } | null) => void }) {
  const [selectedFeature, setSelectedFeature] = useState<Record<string, unknown> | null>(null)
  const [assignmentMode, setAssignmentMode] = useState({ active: false, meterId: null as number | null })
  const [zoomTarget, setZoomTarget] = useState<SearchZoomTarget | null>(null)
  const [searchHighlight, setSearchHighlight] = useState<SearchHighlight | null>(null)
  const [downstreamHighlight, setDownstreamHighlight] =
    useState<import('./types/topology.ts').GeoJSONFeatureCollection<import('./types/topology.ts').GeoJSONPoint> | null>(null)
  // Layer-independent highlight for the selected detail entity (e.g. a meter
  // opened from the transformer panel's Connected Meters list). Drives the blue
  // selection halo even when the Meters layer checkbox is off.
  const [detailHighlight, setDetailHighlight] = useState<{ lat: number; lng: number } | null>(null)
  // Pan request for the selected detail entity / "Show on map" button. The token
  // makes a repeat pan to the same coordinates re-fire; force always recenters.
  const [panRequest, setPanRequest] = useState<{ lat: number; lng: number; force: boolean; token: number } | null>(null)
  const panTokenRef = useRef(0)
  const [notice, setNotice] = useState<string | null>(null)
  const { data: traceData, loading: traceLoading, error: traceError, runTrace, clearTrace } = useTrace()
  const { setSelectedFeederId } = useSelectedFeeder()
  const [searchParams, setSearchParams] = useSearchParams()

  // Transient notice banner (e.g. "No map location on record"): auto-dismiss.
  useEffect(() => {
    if (!notice) return
    const t = window.setTimeout(() => setNotice(null), 4000)
    return () => window.clearTimeout(t)
  }, [notice])

  // Deep-link handoff (item 5): a discrepancy row links to
  // /map?feature=<Type>:<id> (legacy /?feature= bookmarks are redirected here
  // by HomeRoute BEFORE this component can mount — UX spec §3.8, D-3).
  // Read it once into the detail-panel selection, then strip the param so a
  // re-render / refresh does not re-trigger it and the URL stays clean.
  useEffect(() => {
    const parsed = parseFeatureParam(searchParams.get('feature'))
    if (!parsed) return
    setSelectedFeature(parsed)
    const next = new URLSearchParams(searchParams)
    next.delete('feature')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const handleSearchSelect = useCallback((result: SearchResult) => {
    // Type-appropriate zoom: meter 18, transformer 17, substation 16,
    // feeder fitBounds on its route. Null => no geometry on record.
    const target = zoomForSearchResult(result)
    if (target && result.location) {
      setZoomTarget(target)
      setSearchHighlight({ geometry: result.location, label: result.label })
      setNotice(null)
    } else {
      setSearchHighlight(null)
      setNotice(`No map location on record for ${result.label}`)
    }
    // A feeder picked from search also drives the voltage-drop layer flow
    if (result.type === 'feeder') {
      setSelectedFeederId(result.id)
    }
    // Show the result in the detail panel — map search type to _type for detection
    const typeMap: Record<string, string> = {
      meter: 'Meter', transformer: 'Transformer', feeder: 'Feeder', substation: 'Substation',
      device: 'Device',
    }
    setSelectedFeature({
      _type: typeMap[result.type] || result.type,
      id: result.id,
      _searchResult: true,
      name: result.label,
      address: result.sublabel,
    })
  }, [setSelectedFeederId])

  // Clicking a different feature on the map supersedes a search pick: clear the
  // search highlight (orange ring + label) so only the map selection's blue halo
  // shows. handleSearchSelect uses setSelectedFeature directly, so a search pick
  // never clears the highlight it just set.
  const handleMapFeatureSelect = useCallback((feature: Record<string, unknown> | null) => {
    setSearchHighlight(null)
    setSelectedFeature(feature)
  }, [])

  // In-panel navigation (e.g. clicking a meter in the transformer's Connected
  // Meters list). Clears the stale search-result ring so only the newly
  // selected entity's highlight shows.
  const handleFeatureNavigate = useCallback((feature: Record<string, unknown>) => {
    setSearchHighlight(null)
    setSelectedFeature(feature)
  }, [])

  // The selected detail entity reported its own coordinates (meter location).
  const handleDetailLocate = useCallback((coords: { lat: number; lng: number } | null) => {
    setDetailHighlight(coords)
  }, [])

  // Pan the map toward a detail entity. `force` (the "Show on map" button)
  // always recenters; auto-pan only nudges when the point is off-screen.
  const handleDetailPan = useCallback((coords: { lat: number; lng: number }, force = false) => {
    panTokenRef.current += 1
    setPanRequest({ lat: coords.lat, lng: coords.lng, force, token: panTokenRef.current })
  }, [])

  const handleStartAssignment = useCallback((meterId: number) => {
    setAssignmentMode({ active: true, meterId })
    setSelectedFeature(null) // Clear detail panel to show assignment prompt
  }, [])

  const handleCancelAssignment = useCallback(() => {
    setAssignmentMode({ active: false, meterId: null })
  }, [])

  const handleAssignmentComplete = useCallback(() => {
    setAssignmentMode({ active: false, meterId: null })
    setSelectedFeature(null)
  }, [])

  const handleZoomHandled = useCallback(() => {
    setZoomTarget(null)
  }, [])

  const handleDeselect = useCallback(() => {
    setSelectedFeature(null)
    setSearchHighlight(null)
    setDownstreamHighlight(null)
    setDetailHighlight(null)
  }, [])

  // Escape clears the map selection/halo (never the trace). Ignore keypresses
  // while typing in an input/textarea so SearchBar's own Escape stays local.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      handleDeselect()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleDeselect])

  return (
    <div className="flex flex-1 overflow-hidden">
      <Sidebar>
        <div className="p-3 border-b border-gray-200">
          <SearchBar onResultSelect={handleSearchSelect} />
        </div>
        <DetailPanel
          feature={selectedFeature}
          onClose={handleDeselect}
          onFeatureNavigate={handleFeatureNavigate}
          assignmentMode={assignmentMode}
          onStartAssignment={handleStartAssignment}
          onCancelAssignment={handleCancelAssignment}
          onTraceRequest={runTrace}
          traceLoading={traceLoading}
          traceError={traceError}
          onShowDownstream={setDownstreamHighlight}
          onDetailLocate={handleDetailLocate}
          onDetailPan={handleDetailPan}
        />
      </Sidebar>

      <MapView
        onFeatureSelect={handleMapFeatureSelect}
        selectedFeature={selectedFeature}
        onDeselect={handleDeselect}
        searchHighlight={searchHighlight}
        notice={notice}
        assignmentMode={assignmentMode}
        onAssignmentComplete={handleAssignmentComplete}
        zoomTarget={zoomTarget}
        onZoomHandled={handleZoomHandled}
        onMouseCoords={onMouseCoords}
        traceData={traceData}
        onClearTrace={clearTrace}
        downstreamHighlight={downstreamHighlight}
        onClearDownstream={() => setDownstreamHighlight(null)}
        detailHighlight={detailHighlight}
        panRequest={panRequest}
        onPanHandled={() => setPanRequest(null)}
      />
    </div>
  )
}

/** `/` is the Home (attention) page (UX spec §3.8, D-3). Legacy map deep
 *  links (`/?feature=<Type>:<id>`) redirect to /map BEFORE MapPage's
 *  read-once-then-strip effect could ever run, preserving the full query. */
function HomeRoute() {
  const location = useLocation()
  const target = legacyFeatureRedirectTarget(location.search)
  if (target) {
    return <Navigate to={appPath(target)} replace />
  }
  return <AttentionPage />
}

/** The page routes alone. App renders them inside its setup check, /login
 *  route, ProtectedRoute, Header and StatusBar. A host page that mounts the
 *  app under a base path (configureApp) can render these inside its own shell
 *  instead; it then supplies the auth context, a QueryClient,
 *  SelectedFeederProvider and the signed-in gate. Full-height panes (MapPage,
 *  QueuePage) need a flex-column parent. */
export function AppRoutes({ onMouseCoords }: { onMouseCoords: (coords: { lat: number; lng: number } | null) => void }) {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/map" element={<MapPage onMouseCoords={onMouseCoords} />} />
      <Route path="/outages" element={<OutagesDashboard />} />
      <Route path="/outages/history" element={<OutageHistoryPage />} />
      <Route path="/outages/reliability" element={<ReliabilityMetricsPage />} />
      <Route path="/outages/unmatched" element={<UnmatchedSignalsPage />} />
      <Route path="/outages/powertrx" element={<PowertrxExportPage />} />
      <Route path="/engineering" element={<EngineeringDashboard />} />
      <Route path="/secondary-review" element={<SecondaryReviewDashboard />} />
      <Route path="/discrepancies" element={<DiscrepancyDashboard />} />
      <Route path="/gis-queue" element={<QueuePage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/admin/add-user" element={<AddUserPage />} />
      <Route path="/admin/users" element={<UserManagementPage />} />
    </Routes>
  )
}

function App() {
  const { isAuthenticated } = useAuth()
  const [mouseCoords, setMouseCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null) // null = checking

  // Check if first-time setup is needed
  useEffect(() => {
    checkSetupNeeded().then(setNeedsSetup).catch(() => setNeedsSetup(false))
  }, [])

  // Still checking setup status
  if (needsSetup === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  // First-time setup needed
  if (needsSetup && !isAuthenticated) {
    return <SetupPage onSetupComplete={() => setNeedsSetup(false)} />
  }

  return (
    <SelectedFeederProvider>
      <div className="h-screen flex flex-col">
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Header />
                {/*
                  Routed-page container. Must stay a *flex column*, not a plain
                  block: full-height panes (MapPage, QueuePage) size themselves
                  with `flex-1`, which is inert unless this parent is a flex
                  container -- a block parent collapses the map to a thin strip.
                  `min-h-0` lets this shrink inside the h-screen shell so tall
                  document pages (e.g. UserManagementPage) scroll here instead
                  of pushing the StatusBar off-screen.
                */}
                <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
                  <AppRoutes onMouseCoords={setMouseCoords} />
                </div>
                <StatusBar coordinates={mouseCoords} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </SelectedFeederProvider>
  )
}

export default App
